import dayjs from "dayjs";
import { hfbXxxStringTranslator } from "@coworker/locales";
import { TFunction } from "../../../packages/locales/translations/util/useTranslation";
import {
  formatDateByLocale,
  formatDateTimeByLocale,
} from "@coworker/reusable/formatters/date.formatter";
import { NewFile, NewOrExistingFile } from "../shared/types/newOrExistingFile";
import { rotateImageFile } from "./rotateImageFile";
import { ItemNoAndTypeWithOrderedDetails } from "./types/TaskDetailTypes";
import { Loading } from "./loading";

/**
 * Check if a URL is an internal route (relative path or same origin).
 */
export function isInternalUrl(url: string): boolean {
  if (url.startsWith("/")) {
    return true;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * 2025-08-26T12:50:05.251Z -> 2025-08-26 14:50
 * @param iso timestamp in UTC
 * @returns formatted string in user timezone
 */
export function formatDateTime(iso: string) {
  return formatDateTimeByLocale(iso);
}

/**
 * Result will be correctly negative for future dates.
 * @param date e.g. '2026-02-23'
 */
export function calcDaysOverdue(date: string) {
  return dayjs().startOf("day").diff(dayjs(date), "day");
}

/**
 * Only gives a valid result if daysOverdue > 0.
 */
export function calcOverdueString(daysOverdue: number, t: TFunction) {
  if (daysOverdue === 1) {
    return t("oneDayOverdueString");
  } else {
    return t("daysOverdueString", { daysOverdue });
  }
}

type DateProps = {
  dateTime: string;
  t: TFunction;
};

export function timestampToTimeDiff({ dateTime, t }: DateProps): string {
  if (!dateTime) return "";
  const localDate = new Date(dateTime);
  const currentTime = dayjs();
  const timeDifferenceMinutes = currentTime.diff(localDate, "minutes");
  const timeDifferenceHours = currentTime.diff(localDate, "hours");
  const timeDifferenceDays = currentTime
    .startOf("day")
    .diff(dayjs(localDate).startOf("day"), "days");

  const getTimeString = (
    difference: number,
    singularKey: string,
    pluralKey: string
  ) =>
    `${difference} ${difference === 1 ? t(singularKey) : t(pluralKey)} ${t(
      "agoString"
    )}`;

  if (timeDifferenceMinutes === 0) return t("justNowString");
  if (timeDifferenceMinutes < 60)
    return getTimeString(
      timeDifferenceMinutes,
      "minuteString",
      "minutesString"
    );
  if (timeDifferenceHours < 24)
    return getTimeString(timeDifferenceHours, "hourString", "hoursString");
  if (timeDifferenceDays <= 5)
    return getTimeString(timeDifferenceDays, "dayString", "daysString");

  return formatDateByLocale(localDate);
}

/**
 * Generate a unique file name for a rotated file to ensure that the image
 * uploader re-compresses and re-uploads the file instead of using a stale
 * cached version with the same name.
 */
function createRotatedFileName(originalName: string): string {
  const timestamp = Date.now();
  const lastDotIndex = originalName.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return `${originalName}_r${timestamp}`;
  }
  const baseName = originalName.substring(0, lastDotIndex);
  const extension = originalName.substring(lastDotIndex);

  return `${baseName}_r${timestamp}${extension}`;
}

export async function applyRotations(
  images: NewOrExistingFile[]
): Promise<NewOrExistingFile[]> {
  const result: NewOrExistingFile[] = [...images];

  for (let i = 0; i < result.length; i++) {
    const image = result[i]!;
    if (image.rotation === 0) continue;

    if (image.type === "new") {
      const rotatedFile = await rotateImageFile(image.file, image.rotation);
      const renamedFile = new File(
        [rotatedFile],
        createRotatedFileName(image.file.name),
        { type: rotatedFile.type }
      );
      result[i] = { type: "new", file: renamedFile, rotation: 0 };
    } else if (image.type === "existing") {
      try {
        const response = await fetch(image.file.url);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch image "${image.file.name}" for rotation: ${response.status} ${response.statusText}`
          );
        }

        const blob = await response.blob();
        const originalFile = new File([blob], image.file.name, {
          type: blob.type,
        });
        const rotatedFile = await rotateImageFile(originalFile, image.rotation);
        const renamedFile = new File(
          [rotatedFile],
          createRotatedFileName(image.file.name),
          { type: rotatedFile.type }
        );
        const newEntry: NewFile = {
          type: "new",
          file: renamedFile,
          rotation: 0,
        };
        result[i] = newEntry;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        throw new Error(
          `Unable to rotate existing image "${image.file.name}": ${message}`,
          {
            cause: error,
          }
        );
      }
    }
  }

  return result;
}

/**
 * Translates a raw period filter value (e.g. "period=7days") into a
 * human-readable string (e.g. "Last 7 days"). Returns the value unchanged if
 * it does not match the period pattern.
 */
export function formatPeriodFilter(value: string, t: TFunction): string {
  if (value.startsWith("period=")) {
    switch (value) {
      case "period=7days":
        return t("last7DaysString");
      case "period=4weeks":
        return t("last4WeeksString");
      case "period=custom":
        return t("customPeriodString");
      default:
        return value.split("=")[1] ?? value;
    }
  }
  return value;
}

/**
 * Translates a raw location filter value (e.g. "depts=01", "whole=whole",
 * "other=SFM") into a human-readable string (e.g. "HFB 01 - Living room
 * seating"). Returns the value unchanged if it does not match a known pattern.
 */
export function formatLocationFilter(value: string, t: TFunction): string {
  if (value.startsWith("depts=")) {
    const hfbNumber = value.split("=")[1];
    if (!hfbNumber) return value;
    return `HFB ${hfbNumber} - ${hfbXxxStringTranslator(hfbNumber, t)}`;
  }
  if (value.startsWith("whole=")) {
    return t("wholeStoreString");
  }
  if (value.startsWith("other=")) {
    return value.split("=")[1] ?? value;
  }
  return value;
}

export function appendMediaName(
  mediaName: string,
  t: TFunction,
  reason?: string
): string {
  return t("reasonInMediaNameString", { reason, mediaName });
}

/**
 * Ensures every item in the array has pieces_ordered and ptags_ordered
 * initialised. When a field is absent it is seeded from the item's pieces
 * count with completed defaulting to false.
 */
type OrderedDetailsValue =
  { quantity: number; completed: boolean } | null | undefined;

type ItemNoAndTypeWithOrderedDetailsInput = Omit<
  ItemNoAndTypeWithOrderedDetails,
  "pieces_ordered" | "ptags_ordered"
> & {
  pieces_ordered?: OrderedDetailsValue;
  ptags_ordered?: OrderedDetailsValue;
};

export function initializeOrderedDetails(
  value: ItemNoAndTypeWithOrderedDetailsInput[] | null | Loading
): ItemNoAndTypeWithOrderedDetails[] | null | Loading {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((item) => {
    return {
      ...item,
      pieces_ordered: item.pieces_ordered ?? {
        quantity: item.pieces,
        completed: false,
      },
      ptags_ordered: item.ptags_ordered ?? {
        quantity: item.pieces,
        completed: false,
      },
    };
  });
}
